
import {Bank, Contact, Purchase} from "@/models/base";
import AppEditContact from "@/components/contacts/ContactEdit.vue";
import Vue from "vue";
import AppSelectBankType from "@/components/bank/SelectBankType.vue";
import {BankService} from "@/services/bank-service";
import {DossierConfigService} from "@/services/dossier-config-service";
import AppCustomTable from "@/components/common/CustomTable.vue";
import {ISelectItem} from "winfakt-vue-components";
import { printHumanFloat } from "@/utils/pretty-print";
import {
	ExportToIsabelCategoryPurpose,
	ExportToIsabelPriority,
	IExportToIsabelOptions,
	PurchaseService
} from "@/services/purchase-service";
import {BankKind} from "@/models/base/bank";

interface IPurchaseSelection {
	error: string
	errorId: number
	selected: boolean
	purchase?:Purchase;
}

export default Vue.extend({
	filters: {
		printHumanFloat
	},
	data(){
		return {
			bankType: BankService.getType(DossierConfigService.getGeneralConfig().DefaultSepaPaymentBankTypeID) || BankService.getTypes()[0],
			executionDate: new Date(),
			useExpirationDate: false,
			usePaymentDiscountExpirationDate: false,
			priority: "" as ExportToIsabelPriority,
			categoryPurpose: "" as ExportToIsabelCategoryPurpose,
			step: 1,
			selectedPurchases: [] as IPurchaseSelection[] | [],
			toEditPurchase: null as Purchase | null
		};
	},
	props: {
		purchases: {
			type: Array as ()=>Purchase[],
			required: true
		}
	},
	computed: {
		priorityItems():ISelectItem<ExportToIsabelPriority>[]{
			return [
				{
					text: this.$ct("priority-none"),
					value: ""
				},
				{
					text: this.$ct("priority-normal"),
					value: "NORM"
				},
				{
					text: this.$ct("priority-high"),
					value: "HIGH"
				}
			];
		},
		categoryPurposeItems():ISelectItem<ExportToIsabelCategoryPurpose>[] {
			let items:ISelectItem<ExportToIsabelCategoryPurpose>[] = [
				"Dividend",
				"IntraCompanyPayment",
				"Interest",
				"PensionPayment",
				"SalaryPayment",
				"SupplierPayment",
				"TaxPayment",
				"TreasuryPayment"
			].map(v=>{
				return {
					text: this.$ct(`purpose.${v}`),
					value: v
				} as ISelectItem<ExportToIsabelCategoryPurpose>;
			});
			items.unshift({
				text: this.$ct("common.empty"),
				value: ""
			});
			return items;
		},
		selectedAmount():string{
			return printHumanFloat(this.selectedPurchases
				.filter(item => item.selected)
				.reduce((sum, item:IPurchaseSelection) => sum + Number(item.purchase?.TotalOpen ?? 0), 0));
		}
	},
	methods: {
		onSaveSupplier(supplier: Contact) {

			if (!this.selectedPurchases)
				return;

			this.selectedPurchases = this.selectedPurchases.map((p) => {
				if (p.purchase?.ID == this.toEditPurchase?.ID) {
					if (p.purchase) {
						p.purchase.Contact = supplier;
						console.log("EDITED SUPPLIER");
					}
				}
				return p;
			});
			this.validatePurchases();
			this.toEditPurchase = null;
		},
		validateAlreadyPayed() {
			if (!this.selectedPurchases)
				return;

			this.selectedPurchases = this.selectedPurchases.map((p) => {
				if (p.purchase?.ExportedToIsabelDate) {
					p.selected = false;
					p.error = this.$ct("error.already-exported").toString();
					p.errorId = 1;
				}
				return p;
			});
		},
		validateZeroPayments() {
			if (!this.selectedPurchases)
				return;

			this.selectedPurchases = this.selectedPurchases.map((p) => {
				if (p.purchase && p.purchase?.TotalOpen <= 0) {
					p.selected = false;
					p.error = this.$ct("error.amount-error").toString();
					p.errorId = 2;
				}
				return p;
			});
		},
		validateSupplierIban() {
			if (!this.selectedPurchases)
				return;

			this.selectedPurchases = this.selectedPurchases.map((p) => {
				if (!p.purchase?.Contact?.Iban || !this.isValidIBAN(p.purchase?.Contact?.Iban)) {
					p.selected = false;
					p.error = p.purchase?.Contact?.Iban ? this.$ct("error.iban-incorrect").toString() + p.purchase?.Contact?.Iban : this.$ct("error.iban-empty").toString();
					p.errorId = 3;
				} else {
					p.selected = true;
					p.error = "";
					p.errorId = 0;
				}
				return p;
			});
		},
		isValidIBAN(iban: string): boolean {
			if (!iban) return false;
			const sanitized = iban.replace(/\s+/g, "").toUpperCase();

			if (!/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/.test(sanitized)) return false;
			const rearranged = sanitized.slice(4) + sanitized.slice(0, 4);
			const converted = rearranged.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString());
			let total = converted;
			let remainder = "";
			while (total.length > 2) {
				const block = remainder + total.substring(0, 9);
				remainder = (parseInt(block, 10) % 97).toString();
				total = total.substring(9);
			}
			const final = parseInt(remainder + total, 10) % 97;
			return final === 1;
		},
		cancel() {
			this.$emit("close");
		},
		async sumbit() {

			await this.buildSepaPurchases();

			let p = [] as Purchase[];

			this.selectedPurchases?.forEach((purchase) => {
				if (purchase.selected && purchase.purchase) {
					p.push(purchase.purchase);
				}
			});

			let options: IExportToIsabelOptions = {
				executionDate: this.executionDate,
				priority: this.priority,
				useExpirationDate: this.useExpirationDate,
				usePaymentDiscountExpirationDate: this.usePaymentDiscountExpirationDate,
				categoryPurpose: this.categoryPurpose
			};

			await this.deletePaymentDifferenceBankBookings();

			await PurchaseService.exportToIsabel(this.bankType, p, options);

			if (this.usePaymentDiscountExpirationDate) {
				await this.createPaymentDifferenceBankBookings();
			}

			this.$emit("close");
			this.$emit("complete");
		},
		async createPaymentDifferenceBankBookings() {

			//Create bank bookings
			let banks = [] as Bank[];
			let bID = DossierConfigService.getGeneralConfig().DefaultPaymentDifferenceBankTypeID;
			this.selectedPurchases.forEach((iPurchase: IPurchaseSelection) => {
				let item = iPurchase.purchase;
				if (!iPurchase.selected || !item || item.IsCreditNote) return;
				if (item.DiscountPercantage > 0) {
					let amount = (((item.VATPurchaseTradingGoods + item.VATServicesAndCosts + item.VATBuisinessAssets) / 100) * item.DiscountPercantage);
					let bank = new Bank();
					bank.Kind = BankKind.Purchase;
					bank.Total = amount;
					bank.TotalDC = amount * -1;
					bank.ContactID = item.ContactID;
					bank.PurchaseID = item.ID;
					bank.TypeID = bID;
					bank.BookYear = item.BookYear;
					bank.Info = "Betalingskorting";
					banks.push(bank);
				}
			});

			if (banks.length) {
				await BankService.batchPostBooking(banks);
			}

		},
		async deletePaymentDifferenceBankBookings(){
			let bID = DossierConfigService.getGeneralConfig().DefaultPaymentDifferenceBankTypeID;
			let ids = this.purchases.map(p => p.ID);
			if (!ids.length) return;

			// Verse data ophalen zodat we ook boekingen van een vorige generatie zien
			// (de preload bij mounted() kan die missen -> anders dubbele afboeking)
			let freshPurchases = await PurchaseService.getPurchasesById(ids);

			let banks = [] as Bank[];
			freshPurchases.forEach((purchase: Purchase) => {
				purchase.Banks?.forEach((bank: Bank) => {
					if(bank.Info == "Betalingskorting" && bank.TypeID == bID){
						banks.push(bank);
					}
				});
			});

			if (banks.length) {
				await BankService.deleteBookings(banks);
			}

		},
		async createCreditNoteBankBookings(amount:number,purchase:Purchase, referer: string) {

			let bank = new Bank();
			bank.Kind = BankKind.Purchase;
			bank.Total = purchase.IsCreditNote ? amount : amount;
			bank.TotalDC = purchase.IsCreditNote ? amount : amount * -1;
			bank.ContactID = purchase.ContactID;
			bank.PurchaseID = purchase.ID;
			bank.TypeID = this.bankType.ID;
			bank.BookYear = purchase.BookYear;
			bank.Info = purchase.IsCreditNote ? "Verrekend op factuur " + referer : "Verrekening van creditnota " + referer ;

			await BankService.batchPostBooking([bank]);

		},
		next() {
			this.step = 2;
			//let c = this.buildSepaPurchases();
			//console.log("result", c);
		},
		validatePurchases() {
			this.validateAlreadyPayed();
			this.validateZeroPayments();
			this.validateSupplierIban();
		},
		// eslint-disable-next-line max-lines-per-function
		async buildSepaPurchases() {
			function round2(n: number) {
				return Math.abs(n) < 1e-9 ? 0 : Math.round(n * 100) / 100;
			}

			if (!this.selectedPurchases) return;

			const indexed = this.selectedPurchases.map((p, idx) => ({p, idx}));

			const byContact = new Map<string | number, number[]>();
			for (const {p, idx} of indexed) {
				const cid = p?.purchase?.ContactID;
				if (cid == null) continue;
				if (!byContact.has(cid)) byContact.set(cid, []);
				byContact.get(cid)!.push(idx);
			}

			const output: any = [];

			for (const [, indices] of byContact) {
				// clone
				const clones = indices.map(i => {
					const base = this.selectedPurchases[i]!;
					return {...base, purchase: {...base.purchase}};
				});

				// indices binnen de subgroep
				const invoiceIdx: number[] = [];
				const creditIdx: number[] = [];
				clones.forEach((item: any, localIdx: number) => {
					if (item.purchase?.IsCreditNote) creditIdx.push(localIdx);
					else invoiceIdx.push(localIdx);
				});

				for (const ci of creditIdx) {
					const cn = clones[ci].purchase as Purchase;

					if (!cn) continue;

					let cnRemain = Math.max(0, -Number(cn.TotalOpen ?? 0));
					if (cnRemain <= 0) {
						cn.TotalOpen = 0;
						continue;
					}

					for (const ii of invoiceIdx) {
						if (cnRemain <= 0) break;

						const inv = clones[ii].purchase as Purchase;

						if (!inv) continue;

						let invOpen = Math.max(0, Number(inv.TotalOpen ?? 0));

						// Als usePaymentDiscountExpirationDate actief is, wordt de korting apart afgeboekt
						// Dus het effectieve openstaande bedrag is lager
						if (this.usePaymentDiscountExpirationDate && inv.DiscountPercantage > 0) {
							const discount = ((inv.VATPurchaseTradingGoods + inv.VATServicesAndCosts + inv.VATBuisinessAssets) / 100) * inv.DiscountPercantage;
							invOpen = Math.max(0, invOpen - discount);
						}

						if (invOpen <= 0) continue;

						const applied = Math.min(invOpen, cnRemain);
						invOpen -= applied;
						cnRemain -= applied;

						await this.createCreditNoteBankBookings(applied,inv,cn.ComputedFriendlyID || "");
						await this.createCreditNoteBankBookings(applied,cn,inv.ComputedFriendlyID || "");

						inv.TotalOpen = round2(invOpen);
					}

					cn.TotalOpen = round2(-cnRemain);
				}

				for (let k = 0; k < indices.length; k++) {
					const originalIdx = indices[k]!;                 // <- assert: niet undefined
					output[originalIdx] = clones[k]!;                // <- clones[k] bestaat 1-op-1
				}
			}

			for (let i = 0; i < output.length; i++) {
				if (!output[i]) output[i] = this.selectedPurchases[i];
			}

			this.selectedPurchases = output;

		}
	},
	async mounted(){

		let ids = this.purchases.map(s=>s.ID);
		let p = await PurchaseService.getPurchasesById(ids);

		this.selectedPurchases = p.map((p) => {
			return {selected: true, error: "", purchase: p, errorId: 0};
		});
		this.validatePurchases();
	},
	components: {
		AppSelectBankType,
		AppCustomTable,
		AppEditContact
	},
	i18nEx: {
		componentPrefix: "components.purchases.isabel"
	}
});
